import type { ComponentType, ReactNode } from "react";
import { AlertTriangle, Check, Copy, Globe, Sparkles, UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";

function AsideItem({
  Icon,
  title,
  hint,
}: {
  Icon: ComponentType<{ className?: string }>;
  title: string;
  hint: string;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border bg-background">
        <Icon className="h-3 w-3 text-foreground/80" />
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="text-xs font-medium text-foreground">{title}</p>
        <p className="text-[11px] leading-snug text-muted-foreground">{hint}</p>
      </div>
    </div>
  );
}

function ReviewRow({
  label,
  value,
  done = true,
}: {
  label: string;
  value: string;
  done?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      {done ? (
        <Check className="h-3 w-3 shrink-0 text-success-subtle-foreground" aria-hidden="true" />
      ) : (
        <span className="h-3 w-3 shrink-0 rounded-full border border-border" aria-hidden="true" />
      )}
      <span className="flex-1 text-[11px] text-foreground">{label}</span>
      <span className="text-[11px] text-muted-foreground">{value}</span>
    </div>
  );
}

export function NewOrgWhenYouCreateAside({ copyFromOrgName }: { copyFromOrgName?: string }) {
  const { t } = useTranslation();
  const accessTitle = copyFromOrgName
    ? t("iam.newOrgDialog.aside.startsWithAccessFromTitle", "Starts with {{name}}’s access", {
        name: copyFromOrgName,
      })
    : t("iam.newOrgDialog.aside.startsWithAccessTitle", "Starts with Adge’s access");
  const accessHint = copyFromOrgName
    ? t(
        "iam.newOrgDialog.aside.startsWithAccessFromHint",
        "Views and actions are copied from that organization. Trim them in Roles.",
      )
    : t(
        "iam.newOrgDialog.aside.startsWithAccessHint",
        "Views and actions are copied from the super tenant. Trim them in Roles.",
      );

  return (
    <aside className="flex w-[280px] shrink-0 flex-col gap-3 border-l border-border bg-muted px-5 py-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
        {t("iam.newOrgDialog.aside.whenYouCreateHeading", "When you create it")}
      </p>
      <div className="flex flex-col gap-4">
        <AsideItem Icon={Copy} title={accessTitle} hint={accessHint} />
        <AsideItem
          Icon={Globe}
          title={t("iam.newOrgDialog.aside.globalMarketTitle", "Global market included")}
          hint={t(
            "iam.newOrgDialog.aside.globalMarketHint",
            "Every tenant can file Pre Flight reports under Global.",
          )}
        />
        <AsideItem
          Icon={UserPlus}
          title={t("iam.newOrgDialog.aside.inviteAfterTitle", "Invite people right after")}
          hint={t(
            "iam.newOrgDialog.aside.inviteAfterHint",
            "The organization starts empty until you invite its first admin.",
          )}
        />
      </div>
    </aside>
  );
}

export function NewOrgAfterCopyingAside({
  sourceName,
  newName,
}: {
  sourceName: string;
  newName: string;
}) {
  const { t } = useTranslation();
  const target = newName.trim() || t("iam.newOrgDialog.unnamedOrg", "this organization");
  return (
    <aside className="flex w-[280px] shrink-0 flex-col gap-4 border-l border-border bg-muted px-5 py-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
        {t("iam.newOrgDialog.aside.afterCopyingHeading", "After copying")}
      </p>
      <AsideItem
        Icon={Sparkles}
        title={t("iam.newOrgDialog.aside.prefilledTitle", "Steps 2 and 3 are pre-filled")}
        hint={t(
          "iam.newOrgDialog.aside.prefilledHint",
          "Copied values are tagged so you can review and change them before creating.",
        )}
      />
      <div className="flex items-start gap-2.5 rounded-md border border-warning-border bg-warning-subtle px-3 py-2.5">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning-subtle-foreground" />
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-xs font-medium text-warning-subtle-foreground">
            {t("iam.newOrgDialog.aside.partitionTitle", "Set a customer partition after")}
          </p>
          <p className="text-[11px] leading-snug text-warning-subtle-foreground/90">
            {t(
              "iam.newOrgDialog.aside.partitionHint",
              "{{name}} needs its own partition before Sync with BQ can pull products.",
              { name: target },
            )}
          </p>
        </div>
      </div>
      <AsideItem
        Icon={Copy}
        title={t("iam.newOrgDialog.aside.oneTimeTitle", "A one-time copy")}
        hint={t(
          "iam.newOrgDialog.aside.oneTimeHint",
          "Later changes to {{source}} don’t flow into {{target}}.",
          { source: sourceName, target },
        )}
      />
    </aside>
  );
}

export function NewOrgReadyToCreateAside({
  typeLabel,
  productLineLabel,
  industriesSummary,
  marketsSummary,
  defaultBrand,
  accentLabel,
}: {
  typeLabel: string;
  productLineLabel: string;
  industriesSummary: string;
  marketsSummary: string;
  defaultBrand: string;
  accentLabel: string;
}) {
  const { t } = useTranslation();
  return (
    <aside className="flex w-[280px] shrink-0 flex-col gap-3 border-l border-border bg-muted px-5 py-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
        {t("iam.newOrgDialog.aside.readyHeading", "Ready to create")}
      </p>
      <div className="flex flex-col gap-2.5">
        <ReviewRow
          label={t("iam.newOrgDialog.aside.reviewNameType", "Name and type")}
          value={typeLabel}
        />
        <ReviewRow
          label={t("iam.newOrgDialog.productLineLabel", "Product line")}
          value={productLineLabel}
        />
        <ReviewRow
          label={t("iam.newOrgDialog.industriesLabel", "Industries")}
          value={industriesSummary}
        />
        <ReviewRow
          label={t("iam.newOrgDialog.marketsLabel", "Pre Flight markets")}
          value={marketsSummary}
        />
        <ReviewRow
          label={t("iam.newOrgDialog.defaultBrandLabel", "Default brand")}
          value={defaultBrand}
        />
        <ReviewRow
          label={t("iam.newOrgDialog.aside.reviewLogoAccent", "Logo and accent")}
          value={accentLabel}
        />
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        {t("iam.newOrgDialog.aside.editStepsHint", "Click a finished step above to change it.")}
      </p>
    </aside>
  );
}

export function NewOrgDrawerBodyLayout({
  children,
  aside,
}: {
  children: ReactNode;
  aside: ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-8 py-6">{children}</div>
      {aside}
    </div>
  );
}

export function NewOrgSectionHeading({
  title,
  hint,
  trailing,
}: {
  title: string;
  hint?: string;
  trailing?: ReactNode;
}) {
  return (
    <div className={cn("flex flex-col gap-0.5", trailing && "flex-row items-end justify-between gap-2")}>
      <div className="flex min-w-0 flex-col gap-0.5">
        <h4 className="text-[13px] font-semibold text-foreground">{title}</h4>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      {trailing}
    </div>
  );
}
